import { Box, Button, Text } from '@user-portal/wikr-components';
import { trackQuiteFeedbackPopupLoad, trackSendFeedbackClick } from 'analytics/trackers/exercise';
import { useFormik } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';

import isMobile from 'helpers/isMobile';

import Checkbox from 'components/Checkbox';
import CollapsibleBlock from 'components/CollapsibleBlock';
import Modal from 'components/Modal';
import Textarea from 'components/Textarea';

import { reasons } from './constants';
import { shouldDisplayFeedbackForm } from './helpers';
import getValidationSchema from './validation-scheme';

import * as S from './styled';

export type WorkoutReasonAbandonProps = { onClose: () => void };

const ReasonAbandonModal = ({ onClose }: WorkoutReasonAbandonProps) => {
    const { t } = useTranslation();

    const submitSurvey = ({ feedback, reason }: { feedback: string; reason: string }) => {
        trackSendFeedbackClick(reason, feedback);

        onClose();
    };

    const { values, handleChange, handleSubmit, errors, isValid, handleBlur, touched } = useFormik({
        initialValues: { feedback: '', reason: '' },
        validateOnChange: true,
        validateOnBlur: true,
        validationSchema: getValidationSchema(),
        onSubmit: submitSurvey,
    });

    return (
        <Modal
            isOpen
            onClose={onClose}
            fullscreen={isMobile.any()}
            withCloseButton
            width={384}
            trackLoad={trackQuiteFeedbackPopupLoad}
        >
            <Box $paddingTop={32}>
                <Text type="h5" mb={12}>
                    {t('workout.reasonAbandonModal.title')}
                </Text>
                <Text type="medium-text" color="text-secondary">
                    {t('workout.reasonAbandonModal.subtitle')}
                </Text>

                <form onSubmit={handleSubmit}>
                    <Box $paddingTop={12} $paddingBottom={40}>
                        {reasons.map((reason) => {
                            return (
                                <S.QuestionWrapper key={reason.name}>
                                    <Checkbox
                                        type="radio"
                                        name="reason"
                                        checked={reason.name === values.reason}
                                        value={reason.name}
                                        onChange={handleChange}
                                    />
                                    <S.QuestionLabel htmlFor={reason.name}>
                                        <Text color="text-secondary">{t(reason.label)}</Text>
                                    </S.QuestionLabel>
                                </S.QuestionWrapper>
                            );
                        })}

                        <CollapsibleBlock isOpen={shouldDisplayFeedbackForm(values.reason)}>
                            <Box $paddingTop={12} $paddingBottom={0}>
                                <Textarea
                                    name="feedback"
                                    autocomplete="off"
                                    rows="6"
                                    placeholder={t('basics.feedbackAsk')}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    value={values.feedback}
                                    invalid={Boolean(errors.feedback && touched.feedback)}
                                />

                                {Boolean(errors.feedback && touched.feedback) && (
                                    <Text type="caption" color="critical-default" mb={24}>
                                        {errors.feedback}{' '}
                                    </Text>
                                )}
                            </Box>
                        </CollapsibleBlock>
                    </Box>

                    <Button onClick={handleSubmit} disabled={Boolean(!isValid || !values.reason)}>
                        {t('basics.send')}
                    </Button>
                </form>
            </Box>
        </Modal>
    );
};

export default ReasonAbandonModal;
